import {
  getTaxRateByRegion,
  roundTo2Decimals,
  TaxStrategy,
} from '@principle-theorem/accounting';
import {
  IDestinationEntityJobRunOptions,
  ITranslationMap,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  CalculateTreatmentWeight,
  ChartedItemScopeResolver,
  ChartedSurface,
  ChartedTreatment,
  determineTaxFromStrategy,
  FeeSchedule,
  getQuadrant,
  getQuadrantIndex,
  PricedServiceCodeEntry,
  stafferToNamedDoc,
  toINamedDocuments,
  TreatmentConfiguration,
  TreatmentPlan,
  TreatmentStep,
} from '@principle-theorem/principle-core';
import {
  ChartableSurface,
  IChartedTooth,
  IPractice,
  isToothNumber,
  ITreatmentCategory,
  TreatmentConfigurationName,
  TreatmentPlanStatus,
  TreatmentStepStatus,
  type IChartedTreatment,
  type IFeeSchedule,
  type IPatient,
  type IStaffer,
  type IToothRef,
  type ToothNumber,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  getError,
  HISTORY_DATE_FORMAT,
  multiSwitchMap,
  resolveSequentially,
  snapshotCombineLatest,
  sortByCreatedAt,
  sortTimestamp,
  sortTimestampAsc,
  Timestamp,
  Timezone,
  toISODate,
  toMoment,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  where,
  WithPrimaryKey,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, first, groupBy, sortBy, sum, uniqBy } from 'lodash';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { determinePlanStatus } from '../../../d4w/destination/entities/patient-treatment-plans';
import { DestinationEntity } from '../../../destination/destination-entity';
import { feeScheduleResolverFn } from '../../../destination/entities/fee-schedules';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientTreatmentPlanDestinationEntity,
  IPatientTreatmentPlanJobData,
  IPatientTreatmentPlanMigrationData,
  IStepWithIdentifiers,
  ITreatmentPlanStepPair,
  PATIENT_TREATMENT_PLAN_DESTINATION_ENTITY,
} from '../../../destination/entities/patient-treatment-plans';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import {
  determineToothSurfaceByScope,
  resolveMappedCodeTreatmentPair,
  resolveMappTreatmentConfiguration,
} from '../../../mappings/item-codes';
import { PRACTICE_MAPPING } from '../../../mappings/practices';
import { getPractitionerOrDefault } from '../../../mappings/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  OasisPatientAppointment,
  PatientAppointmentSourceEntity,
} from '../../source/entities/patient-appointments';
import {
  OasisPatientNote,
  PatientNoteSourceEntity,
} from '../../source/entities/patient-notes';
import {
  IOasisPatientTreatmentPlanTreatment,
  OasisPatientTreatmentPlanTreatment,
  PatientTreatmentPlanTreatmentsSourceEntity,
} from '../../source/entities/patient-treatment-plan-treatments';
import {
  IOasisPatientTreatment,
  OasisPatientTreatment,
  PatientTreatmentsSourceEntity,
} from '../../source/entities/patient-treatments';
import {
  OasisPatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { OasisNoteDataTransformer } from '../../source/lib/oasis-notes-data-transformer';
import { OasisItemCodeToTreatmentMappingHandler } from '../mappings/item-code-to-treatment';
import { OasisItemCodeMappingHandler } from '../mappings/item-codes';
import { OasisPracticeMappingHandler } from '../mappings/practices';
import { OasisStafferMappingHandler } from '../mappings/staff';
import { PatientDestinationEntity } from './patients';
import { StafferDestinationEntity } from './staff';

export const OASIS_PLAN_NAME = 'OASiS - Migrated Treatments';

const patientTreatmentPlanDestinationEntity =
  DestinationEntity.withMetadataDescription(
    PATIENT_TREATMENT_PLAN_DESTINATION_ENTITY,
    `This migrates the treatments for each patient's appointment and creates a step on a single plan for each appointment.`
  );

type JobData = IPatientTreatmentPlanJobData<OasisPatient> & {
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[];
};

export class PatientTreatmentPlanDestinationEntity extends BasePatientTreatmentPlanDestinationEntity<
  OasisPatient,
  JobData
> {
  override destinationEntity = patientTreatmentPlanDestinationEntity;
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    appointments: new PatientAppointmentSourceEntity(),
    treatments: new PatientTreatmentsSourceEntity(),
    treatmentPlanTreatments: new PatientTreatmentPlanTreatmentsSourceEntity(),
    patientNote: new PatientNoteSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  customMappings = {
    staff: new OasisStafferMappingHandler(),
    practices: new OasisPracticeMappingHandler(),
    itemCodes: new OasisItemCodeMappingHandler(),
    itemCodeToTreatment: new OasisItemCodeToTreatmentMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<JobData[]> {
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const practices$ = this.customMappings.practices.getRecords(translationMap);
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);
    const treatmentConfigurationMappings$ =
      this.customMappings.itemCodeToTreatment.getRecords(translationMap);
    const defaultTreatmentConfiguration$ = brand$.pipe(
      switchMap((brand) =>
        Firestore.firstResult(
          TreatmentConfiguration.col(brand),
          where('name', '==', TreatmentConfigurationName.GeneralADACode)
        )
      ),
      map((config) => {
        if (!config) {
          throw new Error(
            `Couldn't find default treatment configuration ${TreatmentConfigurationName.GeneralADACode}`
          );
        }
        return config;
      })
    );
    const treatmentConfigurations$ = brand$.pipe(
      switchMap((brand) => TreatmentConfiguration.all$(brand))
    );
    const practitioners$ = staff$.pipe(
      multiSwitchMap((staffer) =>
        staffer.destinationIdentifier
          ? Firestore.getDoc(staffer.destinationIdentifier)
          : of(undefined)
      ),
      map(compact),
      toINamedDocuments()
    );
    const treatmentCategories$ = brand$.pipe(
      switchMap((brand) => Brand.treatmentCategories$(brand))
    );
    const taxRate$ = PracticeMigration.organisation$(migration).pipe(
      map((org) => getTaxRateByRegion(org.region))
    );

    return snapshotCombineLatest([
      staff$,
      practitioners$,
      defaultTreatmentConfiguration$,
      treatmentConfigurations$,
      sourceItemCodes$,
      treatmentConfigurationMappings$,
      treatmentCategories$,
      practices$,
      taxRate$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [
            staff,
            practitioners,
            defaultTreatmentConfiguration,
            treatmentConfigurations,
            sourceItemCodes,
            treatmentConfigurationMappings,
            treatmentCategories,
            practices,
            taxRate,
          ],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            sourcePatient,
            staff,
            practitioners,
            defaultTreatmentConfiguration,
            treatmentConfigurations,
            sourceItemCodes,
            treatmentConfigurationMappings,
            treatmentCategories,
            practices,
            taxRate,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: JobData
  ): Promise<
    | IPatientTreatmentPlanMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
        data.sourcePatient.data
      );

      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(data.sourcePatient, 'No patient');
      }

      const sourceTreatments =
        await this.sourceEntities.treatments.filterRecords(
          migration,
          'patientId',
          data.sourcePatient.data.id
        );

      const sourceTreatmentPlanTreatments =
        await this.sourceEntities.treatmentPlanTreatments.filterRecords(
          migration,
          'patientId',
          data.sourcePatient.data.id
        );

      const sourceAppointments =
        await this.sourceEntities.appointments.filterRecords(
          migration,
          'patientId',
          data.sourcePatient.data.id
        );

      const sourcePatientNotes =
        await this.sourceEntities.patientNote.filterRecords(
          migration,
          'patientId',
          sourcePatientId,
          (record) => !!record.data.surfaces
        );

      const planPairs =
        await OasisTreatmentPlanDataBuilder.buildTreatmentPlanData(
          data,
          translationMap,
          this.sourceEntities.appointments,
          sourceTreatments,
          sourceTreatmentPlanTreatments,
          sourceAppointments,
          sourcePatientNotes,
          migration,
          sourcePatientId
        );

      return {
        sourcePatientId: sourcePatientId.toString(),
        patientRef,
        planPairs,
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }
}

export class OasisTreatmentPlanDataBuilder {
  static async buildTreatmentPlanData(
    data: JobData,
    translationMap: TranslationMapHandler,
    appointmentResolver: PatientAppointmentSourceEntity,
    sourceTreatments: IGetRecordResponse<OasisPatientTreatment>[],
    sourceTreatmentPlanTreatments: IGetRecordResponse<OasisPatientTreatmentPlanTreatment>[],
    sourceAppointments: IGetRecordResponse<OasisPatientAppointment>[],
    notesWithSurfaceData: IGetRecordResponse<OasisPatientNote>[],
    migration: WithRef<IPracticeMigration>,
    sourcePatientId: number
  ): Promise<ITreatmentPlanStepPair[]> {
    const defaultFeeSchedule = await FeeSchedule.getPreferredOrDefault(
      migration.configuration.organisation
    );

    const completedSteps: IStepWithIdentifiers[] = [];
    const pendingSteps: IStepWithIdentifiers[] = [];

    const appointmentMatcher = new AppointmentMatchFinder(
      sourceAppointments.sort((appointmentA, appointmentB) =>
        sortTimestampAsc(
          appointmentA.record.translations.from,
          appointmentB.record.translations.from
        )
      ),
      (sourceAppointment) =>
        toISODate(
          sourceAppointment.record.translations.from,
          migration.configuration.timezone
        )
    );

    const planCreatedAt = this.determinePlanCreatedAt(sourceTreatments);
    // const planPractitioner = this.determinePlanPractitioner(
    //   sourceTreatments,
    //   data.staff,
    //   data.practitioners
    // );

    const plan = this.buildPlan(sourcePatientId, planCreatedAt);

    const treatmentStepGroups = groupBy(
      sourceTreatments,
      (treatment) => treatment.data.appointmentDate
    );

    await resolveSequentially(
      Object.values(treatmentStepGroups),
      async (treatments) => {
        await resolveSequentially(treatments, async (treatment) => {
          const chartedTreatments = await this.getChartedTreatments(
            [treatment.data],
            feeScheduleResolverFn(translationMap, defaultFeeSchedule),
            data,
            migration,
            notesWithSurfaceData
          );

          if (!chartedTreatments.length) {
            return;
          }

          const sourceIdentifier = await this.getStepIdentifier(
            migration,
            appointmentResolver,
            sourcePatientId,
            migration.configuration.timezone,
            treatment
          );

          if (!sourceIdentifier) {
            throw new Error(
              `No source identifier found for treatment: ${treatment.record.uid}`
            );
          }

          if (!treatment.record.translations.appointmentDate) {
            return;
          }

          const existingStep = completedSteps.find(
            (step) => step.sourceIdentifier === sourceIdentifier
          );

          if (existingStep) {
            existingStep.treatments.push(...chartedTreatments);
            return;
          }

          const completedStep = await this.buildCompletedStep(
            migration,
            appointmentResolver,
            treatment,
            chartedTreatments,
            data.treatmentCategories,
            sourcePatientId,
            appointmentMatcher
          );

          completedSteps.push(completedStep);
        });

        const backupDate = toMomentTz(
          migration.configuration.backupDate,
          migration.configuration.timezone
        );
        const upcomingAppointments = appointmentMatcher
          .remainingResults()
          .filter((sourceAppointment) =>
            toMoment(sourceAppointment.record.translations.from).isAfter(
              backupDate
            )
          );

        await resolveSequentially(
          upcomingAppointments,
          async (upcomingAppointment) => {
            const appointmentTreatments = sourceTreatmentPlanTreatments.filter(
              (sourceTreatmentPlanTreatment) => {
                if (
                  !sourceTreatmentPlanTreatment.data.appointmentRefId ||
                  !upcomingAppointment.data.treatmentStepId
                ) {
                  return false;
                }

                return (
                  sourceTreatmentPlanTreatment.data.appointmentRefId ===
                  upcomingAppointment.data.treatmentStepId
                );
              }
            );

            const sourceIdentifier = await this.getStepIdentifier(
              migration,
              appointmentResolver,
              sourcePatientId,
              migration.configuration.timezone,
              undefined,
              upcomingAppointment
            );

            if (!sourceIdentifier) {
              throw new Error(
                `No source identifier found for appointment: ${upcomingAppointment.record.uid}`
              );
            }

            const pendingStep: IStepWithIdentifiers =
              await this.buildPendingStep(
                upcomingAppointment,
                appointmentTreatments,
                migration,
                data,
                sourceIdentifier,
                translationMap,
                defaultFeeSchedule,
                notesWithSurfaceData
              );

            const existingStep = pendingSteps.find(
              (step) => step.sourceIdentifier === sourceIdentifier
            );

            if (existingStep) {
              existingStep.treatments = uniqBy(
                [...existingStep.treatments, ...pendingStep.treatments],
                (treatment) => treatment.uuid
              );
              return;
            }

            pendingSteps.push(pendingStep);
          }
        );
      }
    );

    const steps = [
      ...sortBy(completedSteps, (step) => step.date),
      ...sortBy(pendingSteps, (step) => step.date),
    ];

    if (!planCreatedAt) {
      const earliestStepDate = [...steps].sort(sortByCreatedAt).pop()
        ?.createdAt;
      if (earliestStepDate) {
        plan.createdAt = earliestStepDate;
      }
    }

    const status = determinePlanStatus(steps, plan.createdAt);

    const incompletePlanTreatments = sourceTreatmentPlanTreatments.filter(
      (treatment) =>
        !treatment.data.completedAt && !treatment.data.appointmentRefId
    );

    const incompletePlans = await this.buildIncompletePlans(
      incompletePlanTreatments,
      data,
      translationMap,
      defaultFeeSchedule,
      notesWithSurfaceData,
      migration
    );

    return [
      {
        plan: {
          ...plan,
          status,
        },
        steps,
      },
      ...compact(incompletePlans),
    ];
  }

  static async buildIncompletePlans(
    incompletePlanTreatments: IGetRecordResponse<OasisPatientTreatmentPlanTreatment>[],
    data: JobData,
    translationMap: TranslationMapHandler,
    defaultFeeSchedule: WithRef<IFeeSchedule>,
    notesWithSurfaceData: IGetRecordResponse<OasisPatientNote>[],
    migration: WithRef<IPracticeMigration>
  ): Promise<(ITreatmentPlanStepPair | undefined)[]> {
    const incompleteTreatmentPlanGroups = groupBy(
      incompletePlanTreatments,
      (treatment) => treatment.data.treatmentPlanId
    );
    return resolveSequentially(
      Object.values(incompleteTreatmentPlanGroups),
      async (incompletePlanGroup) => {
        const planSourceTreatment = sortBy(
          incompletePlanGroup,
          (treatment) => treatment.data.id
        )[0];
        const planPractitioner =
          OasisNoteDataTransformer.getClinicalNotePractitioner(
            planSourceTreatment.data.practitionerId.toString(),
            data.staff,
            data.practitioners
          );

        const incompleteStepGroups = groupBy(
          incompletePlanGroup,
          (treatment) => treatment.data.stepNumber
        );
        const incompleteSteps: (IStepWithIdentifiers | undefined)[] =
          await resolveSequentially(
            Object.values(incompleteStepGroups),
            async (incompleteStepGroup) => {
              const sortedStepGroup = sortBy(
                incompleteStepGroup,
                (step) => step.data.id
              );
              const sourceTreatment = sortedStepGroup[0];
              const staffer =
                OasisNoteDataTransformer.getClinicalNotePractitioner(
                  sourceTreatment.data.practitionerId.toString(),
                  data.staff,
                  data.practitioners
                );
              const chartedTreatments = await this.getChartedTreatments(
                sortedStepGroup.map((group) => group.data),
                feeScheduleResolverFn(translationMap, defaultFeeSchedule),
                data,
                migration,
                notesWithSurfaceData
              );

              if (!chartedTreatments.length) {
                return;
              }

              const sourceIdentifier = `${sourceTreatment.data.treatmentPlanId.toString()}-step-${
                sortedStepGroup[0].data.stepNumber
              }`;
              const duration = sum(
                sortedStepGroup.map((step) => step.data.minutes ?? 0)
              );

              const treatmentStep = TreatmentStep.init({
                name:
                  sourceTreatment.data.treatmentStepName ??
                  `Step ${sourceTreatment.data.stepNumber}`,
                treatments: chartedTreatments,
                status: TreatmentStepStatus.Incomplete,
                practitionerRef: staffer?.ref,
                schedulingRules: { duration },
              });
              const treatmentCategory =
                await CalculateTreatmentWeight.getPrimaryCategory(
                  treatmentStep,
                  data.treatmentCategories
                );

              return {
                sourceIdentifier,
                ...treatmentStep,
                display: {
                  ...treatmentStep.display,
                  primaryTreatmentCategory: treatmentCategory?.ref,
                },
                createdAt: sourceTreatment.record.translations.createdAt,
                date: toISODate(sourceTreatment.data.createdAt),
              };
            }
          );

        const sourceIdentifier = `${planSourceTreatment.data.patientId}-plan-${planSourceTreatment.data.treatmentPlanId}`;
        const steps = compact(incompleteSteps);
        if (!steps.length) {
          return;
        }
        return {
          plan: {
            ...TreatmentPlan.init({
              name: planSourceTreatment.data.treatmentPlanName,
              practitioner: planPractitioner
                ? stafferToNamedDoc(planPractitioner)
                : undefined,
              createdAt: planSourceTreatment.data.createdAt,
              status: determinePlanStatus(
                steps,
                planSourceTreatment.data.createdAt
              ),
            }),
            sourceIdentifier,
          },
          steps,
        };
      }
    );
  }

  static async buildPendingStep(
    appointment: IGetRecordResponse<OasisPatientAppointment>,
    treatments: IGetRecordResponse<OasisPatientTreatmentPlanTreatment>[],
    migration: WithRef<IPracticeMigration>,
    data: JobData,
    sourceIdentifier: string,
    translationMap: TranslationMapHandler,
    defaultFeeSchedule: WithRef<IFeeSchedule>,
    notesWithSurfaceData: IGetRecordResponse<OasisPatientNote>[]
  ): Promise<IStepWithIdentifiers> {
    const date = toMomentTz(
      appointment.record.translations.from,
      migration.configuration.timezone
    );
    const staffer = OasisNoteDataTransformer.getClinicalNotePractitioner(
      appointment.data.practitionerId.toString(),
      data.staff,
      data.practitioners
    );

    const chartedTreatments = await this.getChartedTreatments(
      treatments.flatMap((treatment) => treatment.data),
      feeScheduleResolverFn(translationMap, defaultFeeSchedule),
      data,
      migration,
      notesWithSurfaceData
    );

    const step = TreatmentStep.init({
      name: `${appointment.data.treatmentSummary} - ${date.format(
        HISTORY_DATE_FORMAT
      )}`,
      treatments: chartedTreatments,
      status: TreatmentStepStatus.Incomplete,
      practitionerRef: staffer?.ref,
    });

    const treatmentCategory = await CalculateTreatmentWeight.getPrimaryCategory(
      step,
      data.treatmentCategories
    );

    const pendingStep: IStepWithIdentifiers = {
      ...step,
      display: {
        ...step.display,
        primaryTreatmentCategory: treatmentCategory?.ref,
      },
      createdAt: appointment.record.translations.createdAt,
      date: toISODate(date),
      sourceIdentifier,
    };
    return pendingStep;
  }

  static async buildCompletedStep(
    migration: WithRef<IPracticeMigration>,
    appointmentResolver: PatientAppointmentSourceEntity,
    treatment: IGetRecordResponse<OasisPatientTreatment>,
    chartedTreatments: IChartedTreatment[],
    treatmentCategories: WithRef<ITreatmentCategory>[],
    sourcePatientId: number,
    appointmentMatcher: AppointmentMatchFinder
  ): Promise<IStepWithIdentifiers> {
    const appointmentDate = treatment.record.translations.appointmentDate;
    const matchingAppointment = appointmentMatcher.findFirstMatch(
      toISODate(appointmentDate, migration.configuration.timezone)
    );

    if (matchingAppointment) {
      appointmentMatcher.removeMatch(matchingAppointment);
    }

    const sourceIdentifier = await this.getStepIdentifier(
      migration,
      appointmentResolver,
      sourcePatientId,
      migration.configuration.timezone,
      treatment
    );

    if (!sourceIdentifier) {
      throw new Error(
        `No source identifier found for treatment: ${treatment.record.uid}`
      );
    }

    const date = toMomentTz(appointmentDate, migration.configuration.timezone);
    const stepName = matchingAppointment
      ? `${matchingAppointment.data.treatmentSummary} ${date.format(
          HISTORY_DATE_FORMAT
        )}`
      : `Completed - ${date.format(HISTORY_DATE_FORMAT)}`;

    const step = TreatmentStep.init({
      name: stepName,
      treatments: chartedTreatments,
      status: TreatmentStepStatus.Complete,
    });
    const treatmentCategory = await CalculateTreatmentWeight.getPrimaryCategory(
      step,
      treatmentCategories
    );

    const completedStep: IStepWithIdentifiers = {
      ...step,
      display: {
        ...step.display,
        primaryTreatmentCategory: treatmentCategory?.ref,
      },
      createdAt: treatment.record.translations.createdAt,
      date: appointmentDate,
      sourceIdentifier,
    };
    return completedStep;
  }

  static buildPlan(
    sourcePatientId: number,
    planCreatedAt?: Timestamp,
    planPractitioner?: INamedDocument<IStaffer>
  ): ITreatmentPlanStepPair['plan'] {
    const sourceIdentifier =
      OasisTreatmentPlanDataBuilder.getPlanIdentifier(sourcePatientId);
    return {
      ...TreatmentPlan.init({
        name: OASIS_PLAN_NAME,
        practitioner: planPractitioner
          ? stafferToNamedDoc(planPractitioner)
          : undefined,
        createdAt: planCreatedAt ?? toTimestamp(),
      }),
      sourceIdentifier,
    };
  }

  static getPlanIdentifier(sourcePatientId: number): string {
    return `${sourcePatientId}-plan`;
  }

  static determinePlanPractitioner(
    treatments: IGetRecordResponse<OasisPatientTreatment>[],
    staff: WithPrimaryKey<ITranslationMap<IStaffer>>[],
    practitioners: INamedDocument<IStaffer>[]
  ): INamedDocument<IStaffer> | undefined {
    const practitioner = getPractitionerOrDefault(
      treatments[0].data.practitionerId,
      staff,
      practitioners
    );

    if (!practitioner) {
      return;
    }

    return stafferToNamedDoc(practitioner);
  }

  static determinePlanCreatedAt(
    treatments: IGetRecordResponse<OasisPatientTreatment>[]
  ): Timestamp | undefined {
    return first(
      compact(
        [...treatments].map(
          (treatment) => treatment.record.translations.createdAt
        )
      )
        .sort(sortTimestamp)
        .reverse()
    );
  }

  static async resolvePractice(
    practices: WithPrimaryKey<ITranslationMap<IPractice>>[],
    treatments: IGetRecordResponse<OasisPatientTreatment>[],
    translationMap: TranslationMapHandler
  ): Promise<WithRef<IPractice>> {
    const sourcePractice = practices.find(
      (practice) =>
        practice.sourceIdentifier === treatments[0].data.locationId?.toString()
    );

    if (!sourcePractice) {
      throw new Error(
        `No practice found for id: ${treatments[0].data.locationId}`
      );
    }

    const practiceRef = await translationMap.getDestination<IPractice>(
      sourcePractice.sourceIdentifier,
      PRACTICE_MAPPING.metadata.type
    );

    if (!practiceRef) {
      throw new Error(
        `No practice found for id: ${sourcePractice.sourceIdentifier}`
      );
    }

    return Firestore.getDoc(practiceRef);
  }

  static buildPlanName(): string {
    return OASIS_PLAN_NAME;
  }

  static getPlanStatus(
    stepStatuses: TreatmentStepStatus[]
  ): TreatmentPlanStatus {
    if (!stepStatuses.length) {
      return TreatmentPlanStatus.Draft;
    }

    return stepStatuses.filter(
      (stepStatus) => stepStatus === TreatmentStepStatus.Complete
    ).length
      ? TreatmentPlanStatus.InProgress
      : TreatmentPlanStatus.Completed;
  }

  static async getChartedTreatments(
    sourceTreatments: (
      | IOasisPatientTreatment
      | IOasisPatientTreatmentPlanTreatment
    )[],
    getPreferredOrDefaultRef: (
      feeSheduleId?: string
    ) => Promise<INamedDocument<IFeeSchedule>>,
    data: Pick<
      JobData,
      | 'staff'
      | 'practitioners'
      | 'sourceItemCodes'
      | 'treatmentConfigurationMappings'
      | 'defaultTreatmentConfiguration'
      | 'taxRate'
    >,
    migration: WithRef<IPracticeMigration>,
    notesWithSurfaceData: IGetRecordResponse<OasisPatientNote>[]
  ): Promise<IChartedTreatment[]> {
    const treatments: IChartedTreatment[] = [];

    await resolveSequentially(sourceTreatments, async (sourceTreatment) => {
      const treatmentProviderId = sourceTreatment.practitionerId.toString();
      const practitioner = getPractitionerOrDefault(
        treatmentProviderId,
        data.staff,
        data.practitioners
      );

      if (!practitioner) {
        throw new Error(
          `Couldn't resolve practitioner for treatment ${treatmentProviderId}`
        );
      }

      let treatmentCode = sourceTreatment.itemCode;
      if (!treatmentCode) {
        return;
      }
      if (treatmentCode.length === 2) {
        treatmentCode = `0${treatmentCode}`;
      }
      const [code, mappedTreatment] = resolveMappedCodeTreatmentPair(
        data.sourceItemCodes,
        data.treatmentConfigurationMappings,
        treatmentCode,
        treatmentCode
      );
      if (!code && !mappedTreatment) {
        // eslint-disable-next-line no-console
        console.log(
          `Couldn't resolve code or treatment from: ${treatmentCode}`
        );
        return;
      }

      const treatmentConfiguration = await resolveMappTreatmentConfiguration(
        data.defaultTreatmentConfiguration,
        mappedTreatment
      );

      const feeSchedule = await getPreferredOrDefaultRef();
      const taxStatus = TaxStrategy.GSTFree;
      const calculatedPrice =
        (sourceTreatment.amount ?? 0) / (sourceTreatment.quantity ?? 1);
      const price = roundTo2Decimals(calculatedPrice);

      const toothNumber = sourceTreatment.toothNumber
        ? String(sourceTreatment.toothNumber).trim()
        : undefined;
      if (!isToothNumber(toothNumber) && toothNumber) {
        // eslint-disable-next-line no-console
        console.info(`${toothNumber} isn't a valid tooth number`);
      }

      let foundNotes = notesWithSurfaceData.filter((note) => {
        const isSamePractitioner =
          note.data.practitionerId?.toString() === treatmentProviderId;
        const isSameDate =
          OasisNoteDataTransformer.getOasisNoteDate(
            note,
            migration.configuration.timezone
          ) === sourceTreatment.createdAt;
        const isSameTooth = note.data.toothNumber?.toString() === toothNumber;
        return isSamePractitioner && isSameDate && isSameTooth;
      });

      if (foundNotes.length > 1) {
        foundNotes = foundNotes.filter((note) => {
          if (!note.data.serviceCode) {
            return true;
          }
          return note.data.serviceCode === sourceTreatment.itemCode;
        });
      }

      if (foundNotes.length > 1) {
        // eslint-disable-next-line no-console
        console.log(
          `Found ${foundNotes.length} notes for treatment ${sourceTreatment.id}`
        );
      }

      const noteSurfaces =
        foundNotes.length === 1 ? foundNotes[0].data.surfaces : undefined;
      const surfaces =
        'surfaces' in sourceTreatment && sourceTreatment.surfaces?.length
          ? sourceTreatment.surfaces
          : noteSurfaces;

      const scopeResolver = new ChartedItemScopeResolver();
      const chartedSurfaces = isToothNumber(toothNumber)
        ? this.getChartedRefs({
            toothNumber,
            surfaces,
          }).map((chartedRef) => {
            const chartedSurface = ChartedSurface.init({
              chartedBy: stafferToNamedDoc(practitioner),
              resolvedBy:
                'appointmentDate' in sourceTreatment
                  ? this.getResolvedBy(sourceTreatment, practitioner)
                  : undefined,
              resolvedAt:
                'appointmentDate' in sourceTreatment
                  ? toTimestamp(
                      toMomentTz(
                        sourceTreatment.appointmentDate,
                        migration.configuration.timezone
                      )
                    )
                  : undefined,
              chartedRef,
            });

            return determineToothSurfaceByScope(
              chartedSurface,
              treatmentConfiguration,
              scopeResolver
            );
          })
        : [];

      const serviceCode = code
        ? PricedServiceCodeEntry.init({
            code: code.code,
            type: code.type,
            taxStatus,
            price,
            quantity: sourceTreatment.quantity,
            tax: determineTaxFromStrategy(data.taxRate, {
              taxStatus: taxStatus,
              amount: price,
            }),
            chartedSurfaces,
          })
        : undefined;

      const scopeRefs = scopeResolver.reduceChartedSurfacesToScope(
        treatmentConfiguration,
        chartedSurfaces
      );
      const scopeRef = first(scopeRefs)?.scopeRef ?? {
        scope: ChartableSurface.WholeMouth,
      };

      treatments.push(
        ChartedTreatment.init({
          uuid: sourceTreatment.id.toString(),
          config: toNamedDocument(treatmentConfiguration),
          feeSchedule,
          serviceCodes: serviceCode ? [serviceCode] : [],
          chartedSurfaces,
          attributedTo: stafferToNamedDoc(practitioner),
          scopeRef,
          resolvedBy:
            'appointmentDate' in sourceTreatment
              ? this.getResolvedBy(sourceTreatment, practitioner)
              : undefined,
          resolvedAt:
            'appointmentDate' in sourceTreatment
              ? toTimestamp(
                  toMomentTz(
                    sourceTreatment.appointmentDate,
                    migration.configuration.timezone
                  )
                )
              : undefined,
        })
      );
    });

    return treatments;
  }

  static async getStepIdentifier(
    migration: WithRef<IPracticeMigration>,
    appointmentResolver: PatientAppointmentSourceEntity,
    sourcePatientId: number,
    timezone: Timezone,
    treatment?: IGetRecordResponse<OasisPatientTreatment>,
    appointment?: IGetRecordResponse<OasisPatientAppointment>,
    patientAppointments?: IGetRecordResponse<OasisPatientAppointment>[]
  ): Promise<string> {
    if (treatment) {
      const completedDate = toISODate(
        treatment.record.translations.appointmentDate,
        timezone
      );

      return `${sourcePatientId}-${completedDate}`;
    }

    if (!appointment) {
      throw new Error('No appointment or treatment provided');
    }

    const appointmentDate = toISODate(
      appointment.record.translations.from,
      timezone
    );

    const sameDayAppointments = (
      patientAppointments ??
      (await appointmentResolver.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      ))
    ).filter(
      (filteredAppointment) =>
        toISODate(filteredAppointment.record.translations.from, timezone) ===
        appointmentDate
    );

    const sortedAppointments = [...sameDayAppointments].sort(
      (appointmentA, appointmentB) =>
        sortTimestampAsc(
          appointmentA.record.translations.from,
          appointmentB.record.translations.from
        )
    );

    const appointmentIndex = sortedAppointments.findIndex(
      (sortedAppointment) =>
        sortedAppointment.record.uid === appointment.record.uid
    );

    if (appointmentIndex < 1) {
      return `${sourcePatientId}-${appointmentDate}`;
    }

    return `${sourcePatientId}-${appointmentDate}-${appointmentIndex + 1}`;
  }

  static getResolvedBy(
    procedure: IOasisPatientTreatment,
    practitioner: WithRef<IStaffer> | INamedDocument<IStaffer>
  ): INamedDocument<IStaffer> | undefined {
    return procedure.appointmentDate
      ? stafferToNamedDoc(practitioner)
      : undefined;
  }

  static getChartedRefs(treatment: {
    toothNumber?: ToothNumber;
    surfaces?: ChartableSurface[];
  }): (
    | { unscoped: true }
    | { tooth: IToothRef }
    | { tooth: Required<IChartedTooth> }
  )[] {
    const quadrant = treatment.toothNumber
      ? getQuadrant(treatment.toothNumber)
      : undefined;
    const quadrantIndex = treatment.toothNumber
      ? getQuadrantIndex(treatment.toothNumber)
      : undefined;
    if (!treatment.toothNumber || !quadrant || !quadrantIndex) {
      return [
        {
          unscoped: true,
        },
      ];
    }

    const toothRef: IToothRef = {
      quadrant,
      quadrantIndex,
    };

    if (!treatment.surfaces) {
      return [
        {
          tooth: toothRef,
        },
      ];
    }

    return treatment.surfaces.map((surface) => ({
      tooth: {
        ...toothRef,
        surface,
      },
    }));
  }
}

export class AppointmentMatchFinder {
  private _appointments: IGetRecordResponse<OasisPatientAppointment>[];

  constructor(
    appointments: IGetRecordResponse<OasisPatientAppointment>[],
    private _getComparisonValueFn: (
      appointment: IGetRecordResponse<OasisPatientAppointment>
    ) => string
  ) {
    this._appointments = [...appointments];
  }

  remainingResults(): IGetRecordResponse<OasisPatientAppointment>[] {
    return [...this._appointments];
  }

  findMatches(
    comparisonValue: string
  ): IGetRecordResponse<OasisPatientAppointment>[] {
    return this._appointments.filter(
      (appointment) =>
        this._getComparisonValueFn(appointment) === comparisonValue
    );
  }

  findFirstMatch(
    comparisonValue: string
  ): IGetRecordResponse<OasisPatientAppointment> | undefined {
    return this._appointments.find(
      (appointment) =>
        this._getComparisonValueFn(appointment) === comparisonValue
    );
  }

  removeMatch(appointment: IGetRecordResponse<OasisPatientAppointment>): void {
    this._appointments = this._appointments.filter(
      (existingAppointment) =>
        existingAppointment.record.uid !== appointment.record.uid
    );
  }
}
